import { LidoWithdrawalQueueAbi } from '../abis/lido'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, toNumber, type LstWithdrawalReader } from './shared'

/**
 * Lido stETH/wstETH — FIFO withdrawal queue, NFT-enumerable.
 *
 * Two stages:
 *  1. `WithdrawalQueueERC721.getWithdrawalRequests(owner) → uint256[]`
 *     — enumerate request IDs owned by the user.
 *  2. `WithdrawalQueueERC721.getWithdrawalStatus(ids[]) → struct[]` +
 *     `getLastFinalizedRequestId()` — get per-request status + the
 *     frontier for queue-position computation.
 *
 * `amountUnderlying` = `amountOfStETH` (raw wei stETH at request time;
 * exact ETH payout is determined at finalization).
 */
export const readerLido: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    if (!entry.withdrawalContract) return []

    // Stage 1: enumerate.
    const stage1 = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'getWithdrawalRequests',
          params: [user],
        },
        {
          address: entry.withdrawalContract,
          name: 'getLastFinalizedRequestId',
          params: [],
        },
      ] as unknown as never[],
      abi: [LidoWithdrawalQueueAbi, LidoWithdrawalQueueAbi] as unknown as never,
    })) as unknown[]

    const ids = stage1[0] as unknown[] | undefined
    const frontier = toBigInt(stage1[1]) ?? 0n
    if (!Array.isArray(ids) || ids.length === 0) return []

    // Stage 2: status batch.
    const stage2 = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'getWithdrawalStatus',
          params: [ids],
        },
      ] as unknown as never[],
      abi: [LidoWithdrawalQueueAbi] as unknown as never,
    })) as unknown[]

    const statuses = stage2[0] as Array<Record<string, unknown>> | undefined
    if (!Array.isArray(statuses)) return []

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < ids.length; i++) {
      const id = toBigInt(ids[i])
      const s = statuses[i]
      if (id === undefined || !s) continue
      const amountOfStETH = toBigInt(s.amountOfStETH)
      const isFinalized = Boolean(s.isFinalized)
      const isClaimed = Boolean(s.isClaimed)
      if (amountOfStETH === undefined) continue
      if (isClaimed) {
        // Lido's getter retains claimed entries — surface for
        // historical / audit display.
        out.push({
          lst: entry.lst,
          brand: entry.brand,
          symbol: entry.symbol,
          requestId: id.toString(),
          amountUnderlying: amountOfStETH.toString(),
          status: 'claimed',
        })
        continue
      }
      const queuePosition = isFinalized ? 0 : Number(id - frontier)
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: id.toString(),
        amountUnderlying: amountOfStETH.toString(),
        status: isFinalized ? 'claimable' : 'pending',
        queuePosition: queuePosition > 0 ? queuePosition : undefined,
      })
    }
    // queuePosition is a uint diff; ignore if it overflowed Number.
    return out.filter(
      (r) => r.queuePosition === undefined || !Number.isNaN(r.queuePosition),
    )
    // Note: `toNumber` would be cleaner but we want signed semantics
    // — `frontier - id` for already-finalized entries.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    void toNumber
  },
}
